/**
 * Register the Lumyn service worker.
 * Call this from index.js on app startup.
 */
export async function registerServiceWorker() {
  const isDev = process.env.NODE_ENV !== 'production';
  if (!('serviceWorker' in navigator)) {
    if (isDev) console.log('[PWA] Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    if (isDev) console.log('[PWA] Service worker registered:', registration.scope);

    // Check for updates every 60 minutes. update() can reject on a transient
    // network/server blip (surfaces in browsers as "Failed to update a
    // ServiceWorker…"); catch it so it never becomes an unhandled rejection.
    // The browser retries automatically on the next interval.
    setInterval(() => {
      registration.update().catch((err) => {
        if (isDev) console.debug('[PWA] SW update check failed (will retry):', err?.message || err);
      });
    }, 60 * 60 * 1000);

    // Listen for new service worker waiting
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      newWorker?.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          window.dispatchEvent(new CustomEvent('sw-update-available'));
        }
      });
    });

    return registration;
  } catch (error) {
    console.error('[PWA] Registration failed:', error);
    return null;
  }
}

/**
 * Request notification permission and subscribe to push.
 */
export async function subscribeToPush(registration) {
  if (!('PushManager' in window)) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  try {
    // Fetch VAPID key from backend
    const resp = await fetch('/api/v1/push/vapid-key');
    const { public_key } = await resp.json();
    if (!public_key) {
      console.warn('[PWA] No VAPID key configured');
      return null;
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
    if (process.env.NODE_ENV !== 'production') console.log('[PWA] Push subscription:', subscription);
    return subscription;
  } catch (error) {
    console.error('[PWA] Push subscription failed:', error);
    return null;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}
